export type ProvisioningJobStatus =
    | 'DISPATCHING'
    | 'DISPATCH_FAILED'
    | 'QUEUED'
    | 'RUNNING'
    | 'SUCCEEDED'
    | 'VERIFYING'
    | 'VERIFIED'
    | 'VERIFY_FAILED'
    | 'FAILED'
    | 'CANCELLED'
    | 'TIMED_OUT';

export type ProvisioningErrorCode =
    | 'UNREACHABLE'
    | 'AUTH_FAILED'
    | 'TASK_FAILED'
    | 'DNS_PREFLIGHT'
    | 'TIMEOUT'
    | 'INTERRUPTED'
    | 'INTERNAL';

export const IN_FLIGHT_JOB_STATUSES: ProvisioningJobStatus[] = [
    'DISPATCHING',
    'QUEUED',
    'RUNNING',
    'VERIFYING',
];

export const isTerminalJobStatus = (s?: ProvisioningJobStatus | null): boolean =>
    !!s && !IN_FLIGHT_JOB_STATUSES.includes(s);

export interface ProvisioningRunStats {
    ok: number;
    changed: number;
    failures: number;
    unreachable: number;
    skipped: number;
    duration_ms: number;
}

export interface ProvisioningEvent {
    counter: number;
    ts: string;
    type: string;
    level: 'debug' | 'info' | 'warning' | 'error';
    task?: string | null;
    host?: string | null;
    message: string;
    ok?: boolean | null;
    changed?: boolean | null;
}

export interface ProvisioningJob {
    id: string;
    deploymentId: string;
    attempt: number;
    status: ProvisioningJobStatus;
    sshUser: string;
    usedCredentialOverride: boolean;
    lastEventCounter: number;
    verifyAttempt: number;
    queuedAt: string;
    startedAt?: string | null;
    finishedAt?: string | null;
    verifiedAt?: string | null;
    errorMessage?: string | null;
    errorCode?: ProvisioningErrorCode | null;
    stats?: ProvisioningRunStats | null;
    healthReport?: { domain: string; ok: boolean; status?: number; error?: string }[] | null;
    triggeredBy?: {
        id: string;
        email: string;
        firstName?: string | null;
        lastName?: string | null;
    } | null;
}

export interface ProvisioningLogPage {
    jobId: string;
    deploymentId: string;
    status: ProvisioningJobStatus;
    lastCounter: number;
    events: ProvisioningEvent[];
}

export interface ProvisionDeploymentDto {
    adminEmail: string;
    adminPassword: string;
    sshUser?: string;
    sshPort?: number;
    sshCredential?: { type: 'PASSWORD' | 'PRIVATE_KEY'; secret: string };
    persistSshCredential?: boolean;
}

export interface SshCredentialSummary {
    type: 'PASSWORD' | 'PRIVATE_KEY';
    keyVersion: number;
    updatedAt: string;
}

/** Socket payloads. Both carry `jobId` so a handler can filter, matching the
 *  `data.paymentId === paymentId` idiom in checkout-form.tsx. */
export interface ProvisioningEventsPush {
    jobId: string;
    deploymentId: string;
    events: ProvisioningEvent[];
}

export interface ProvisioningStatusPush {
    jobId: string;
    deploymentId: string;
    status: ProvisioningJobStatus;
    deploymentStatus: string;
    verifyAttempt?: number;
    errorMessage?: string | null;
    errorCode?: ProvisioningErrorCode | null;
    healthReport?: { domain: string; ok: boolean; status?: number; error?: string }[] | null;
}
